import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { updateChannelModuleMutation } from 'lane-shared/graphql/modules';
import type { WorkOrderModuleConfiguration } from 'lane-shared/domains/workOrder/types/workOrder';
import { ModuleCategoryEnum } from 'lane-shared/types/modules/modulesEnums';
import type { Channel } from 'lane-shared/types/ChannelType';
import { useWorkOrderValidationSchema } from './useWorkOrderValidationSchema';
import { ValidationError } from 'yup';

export const useWorkOrderConfigurationForm = (
  channel?: Pick<Channel, '_id' | 'channelModules'>,
  refetchChannel = () => {}
) => {
  const { t } = useTranslation();
  const [updateChannelModule] = useMutation(updateChannelModuleMutation);
  const schema = useWorkOrderValidationSchema();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const workOrdersModule = channel?.channelModules?.find(
    module => module.name === ModuleCategoryEnum.WorkOrders
  );
  const moduleConfiguration = workOrdersModule?.settings?.configuration;

  const initialConfiguration: WorkOrderModuleConfiguration = {
    messageNotification: {
      tenantChat: moduleConfiguration?.messageNotification?.tenantChat ?? false,
    },
    workOrderSurveys: {
      allowFeedback:
        moduleConfiguration?.workOrderSurveys?.allowFeedback ?? false,
      autoSurveyTriggerStatus:
        moduleConfiguration?.workOrderSurveys?.autoSurveyTriggerStatus ?? null,
    },
  };

  const form = useForm<WorkOrderModuleConfiguration>({
    defaultValues: initialConfiguration,
    mode: 'onChange',
    shouldUnregister: false,
    resolver: async data => {
      try {
        await schema.validate(data, { abortEarly: false });

        return { values: data, errors: {} };
      } catch (err: unknown) {
        const errors: Record<string, { message: string }> = {};

        if (err instanceof ValidationError && err.inner?.length > 0) {
          err.inner.forEach(e => {
            if (e.path) {
              errors[e.path] = { message: e.message };
            }
          });
        }

        return { values: data, errors };
      }
    },
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty, isValid },
  } = form;

  const saveConfiguration = handleSubmit(async data => {
    // Prevent concurrent submissions
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Validate that the work orders module exists and has required fields
      if (!workOrdersModule) {
        throw new Error(
          'Work Orders module not found. Cannot update configuration.'
        );
      }

      if (!workOrdersModule.friendlyName) {
        throw new Error(
          'Work Orders module is missing required friendlyName field.'
        );
      }

      if (!channel?._id) {
        throw new Error(
          'Channel ID is required to update module configuration.'
        );
      }

      // Send minimal payload - only required fields and what we're updating
      await updateChannelModule({
        variables: {
          channelModule: {
            _id: workOrdersModule._id,
            name: workOrdersModule.name,
            friendlyName: workOrdersModule.friendlyName,
            channelId: channel._id,
            settings: {
              ...workOrdersModule.settings,
              configuration: data,
            },
          },
        },
      });

      await refetchChannel();
      reset(data);

      window.Toast.show(
        t('web.admin.serviceRequest.settings.toast.successMessage')
      );
    } catch (ex) {
      console.error(ex);

      // Show user-friendly error message
      const errorMessage =
        ex instanceof Error ? ex.message : 'Failed to save configuration';

      window.Toast.show(errorMessage, 5000);
    } finally {
      setIsSubmitting(false);
    }
  });

  const undoConfiguration = () => reset(initialConfiguration);

  return {
    form,
    isDirty,
    isValid,
    isSubmitting,
    saveConfiguration,
    undoConfiguration,
  };
};
